import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Trash2, Check, X, Download, CornerDownLeft } from "react-feather";
import { useTheme } from "../theme/ThemeContext";
import { PERIODS, isInPeriod, periodLabel } from "../utils/period";
import SortSelect from "../components/SortSelect";
import { sortByOption, DEFAULT_SORT } from "../utils/sort";

import { EXPENSE_TYPES, ALL_EXPENSE_TYPES, INCOME_TYPES } from "../utils/expenseTypes";
import { BILL_FREQUENCIES, MONTH_NAMES, repeatUnitWord, currentPeriodDate, nextOccurrence } from "../utils/billFrequency";
import { toCsv, saveCsv, TRANSACTION_COLUMNS } from "../utils/csv";
import { paidFromName } from "../utils/bills";
import { sourceRequired, bankAccountsOf, paidFromFields, receivedIntoId, missingPaidFrom, missingReceivedInto } from "../utils/moneySources";
import MoneySourceSelect from "../components/MoneySourceSelect";
import { useMoneySources } from "../components/useMoneySources";

import { API_BASE, apiFetch } from "../api";
// Income that can come on a schedule; a gift, borrowed money or "other" is a one-off.
const SCHEDULED_INCOME = ["Salary", "Payment"];
const ALL_TYPES = [...new Set([...ALL_EXPENSE_TYPES, ...INCOME_TYPES])];
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

// Type (and, for a salary or payment, how often it comes) start on "Select" and
// have to be chosen.
const emptyForm = () => ({
  kind: "income",
  label: "",
  type: "",
  date: "",
  amount: "",
  autopay: false,
  description: "",
  repeatUnit: "",
  repeatEvery: "1",
  dueDay: String(new Date().getDate()),
  dueMonth: String(new Date().getMonth()),
  // "Paid from" for an expense, "Received into" for income (see MoneySourceSelect).
  source: "",
});

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const formatDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const normalizeBill = (b) => ({
  id: `bill-${b.id}`,
  rawId: b.id,
  kind: "expense",
  date: b.dueDate,
  label: b.payee,
  type: b.type,
  amount: b.amount,
  status: b.status,
  autopay: !!b.autopay,
  description: b.description,
  repaid: !!b.repaidIncomeId,
  paidFromKind: b.paidFromKind,
  paidFromId: b.paidFromId,
  raw: b,
});

const normalizeIncome = (i) => ({
  id: `income-${i.id}`,
  rawId: i.id,
  kind: "income",
  date: i.date,
  label: i.source,
  type: i.type,
  amount: i.amount,
  status: i.status,
  description: i.description,
  repaid: !!i.repaidBillId,
  raw: i,
});

const Transactions = () => {
  const { tokens, fonts } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const labelFilter = searchParams.get("label") || "";

  const [bills, setBills] = useState([]);
  const [income, setIncome] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState(labelFilter ? "All" : "Month");
  const [typeFilter, setTypeFilter] = useState("All");
  const [sortBy, setSortBy] = useState(DEFAULT_SORT);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  // Income just saved whose date has already come: did it arrive?
  const [askReceived, setAskReceived] = useState(null);
  const sources = useMoneySources();
  // Lent money coming back / borrowed money paid back: { item, kind, source }.
  const [settle, setSettle] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      apiFetch(`${API_BASE}/bills`).then((res) => (res.ok ? res.json() : Promise.reject())),
      apiFetch(`${API_BASE}/income`).then((res) => (res.ok ? res.json() : Promise.reject())),
    ])
      .then(([billsData, incomeData]) => {
        setBills(billsData);
        setIncome(incomeData);
      })
      .catch(() =>
        setError("Couldn't reach the backend. Make sure it's running with `npm run server`.")
      )
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const entries = sortByOption(
    [...bills.map(normalizeBill), ...income.map(normalizeIncome)]
      .filter((item) => isInPeriod(item.date, period))
      .filter((item) => typeFilter === "All" || item.type === typeFilter)
      .filter((item) => !labelFilter || item.label === labelFilter),
    sortBy
  );

  const clearLabelFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("label");
    setSearchParams(next);
  };

  const inputStyle = { ...styles.formInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface };
  const incomeScheduled = form.kind === "income" && SCHEDULED_INCOME.includes(form.type);

  const totalIncome = entries.filter((e) => e.kind === "income").reduce((sum, e) => sum + e.amount, 0);
  const totalExpenses = entries.filter((e) => e.kind === "expense").reduce((sum, e) => sum + e.amount, 0);
  const net = totalIncome - totalExpenses;

  const handleToggleStatus = (item) => {
    const nextStatus =
      item.kind === "expense"
        ? item.status === "Paid" ? "Pending" : "Paid"
        : item.status === "Received" ? "Upcoming" : "Received";

    const endpoint = item.kind === "expense" ? "bills" : "income";

    if (item.kind === "expense") {
      setBills((prev) => prev.map((b) => (b.id === item.rawId ? { ...b, status: nextStatus } : b)));
    } else {
      setIncome((prev) => prev.map((i) => (i.id === item.rawId ? { ...i, status: nextStatus } : i)));
    }

    // Paying a repeating bill (or receiving a repeating income) lines up the next one
    // on the server, so reload to show it.
    apiFetch(`${API_BASE}/${endpoint}/${item.rawId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: nextStatus }),
    })
      .then(() => (nextStatus === "Paid" || nextStatus === "Received") && load())
      .catch(() => load());
  };

  // Money lent came back (recorded as money received, into the account chosen), or
  // money borrowed was paid back (from the account / card chosen).
  const settleRequired = settle && sourceRequired(todayIso(), sources.assets, sources.cards) && (settle.kind === "borrowed" || bankAccountsOf(sources.assets).length > 0);
  const confirmSettle = () => {
    if (settleRequired && !settle.source) return;
    const { item, kind, source } = settle;
    const path = kind === "lent" ? `bills/${item.rawId}/repaid` : `income/${item.rawId}/repaid`;
    const body = kind === "lent" ? { receivedIntoId: receivedIntoId(source) } : paidFromFields(source);
    setSettle(null);
    apiFetch(`${API_BASE}/${path}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }).finally(load);
  };

  // An entry dated after the first account / card was added that doesn't say where
  // the money went: set it right there in the list.
  const setSource = (item, value) => {
    const body = item.kind === "income" ? { receivedIntoId: receivedIntoId(value) } : paidFromFields(value);
    apiFetch(`${API_BASE}/${item.kind === "income" ? "income" : "bills"}/${item.rawId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).finally(load);
  };

  const answerReceived = (received) => {
    const entry = askReceived;
    setAskReceived(null);
    if (!received) return;
    apiFetch(`${API_BASE}/income/${entry.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: "Received" }),
    }).finally(load);
  };

  const exportCsv = () =>
    saveCsv(`budgetsprint-transactions-${periodLabel(period).toLowerCase().replace(/\s+/g, "-")}.csv`, toCsv(entries, TRANSACTION_COLUMNS));

  const handleDelete = (item) => {
    const endpoint = item.kind === "expense" ? "bills" : "income";
    if (item.kind === "expense") {
      setBills((prev) => prev.filter((b) => b.id !== item.rawId));
    } else {
      setIncome((prev) => prev.filter((i) => i.id !== item.rawId));
    }
    apiFetch(`${API_BASE}/${endpoint}/${item.rawId}`, { method: "DELETE" }).catch(() => load());
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setFormError(null);

    const amount = parseFloat(form.amount);
    const isIncome = form.kind === "income";
    const scheduled = isIncome && SCHEDULED_INCOME.includes(form.type);
    const repeats = scheduled && form.repeatUnit && form.repeatUnit !== "once";
    const repeatEvery = repeats ? parseInt(form.repeatEvery, 10) : 1;
    // Monthly: this month's day; yearly: this year's month and day (either may have
    // passed already - then we ask). Otherwise the date picked.
    const date =
      repeats && form.repeatUnit !== "days"
        ? currentPeriodDate(form.repeatUnit, { day: parseInt(form.dueDay, 10), month: parseInt(form.dueMonth, 10) })
        : form.date;
    if (!form.label.trim()) return setFormError(isIncome ? "Source is required." : "Payee is required.");
    if (!form.type) return setFormError("Choose a type.");
    if (scheduled && !form.repeatUnit) return setFormError("Choose how often it comes.");
    if (repeats && (Number.isNaN(repeatEvery) || repeatEvery < 1 || repeatEvery > 365)) return setFormError("Repeats every must be from 1 to 365.");
    if (!date) return setFormError("Date is required.");
    if (Number.isNaN(amount) || amount < 0) return setFormError("Amount must be a valid number.");
    const needSource = sourceRequired(date, sources.assets, sources.cards) && (!isIncome || bankAccountsOf(sources.assets).length > 0);
    if (needSource && !form.source) return setFormError(isIncome ? "Choose the account it goes into." : "Choose where it's paid from.");

    setSaving(true);

    const endpoint = isIncome ? "income" : "bills";
    const hasCome = date <= todayIso();
    // A salary or payment is expected (Upcoming) until confirmed; a gift / "other"
    // is money already in hand; an expense is money already spent.
    const body = isIncome
      ? {
          source: form.label,
          type: form.type,
          date,
          amount,
          description: form.description,
          status: scheduled || !hasCome ? "Upcoming" : "Received",
          repeatUnit: repeats ? form.repeatUnit : "once",
          repeatEvery,
          receivedIntoId: receivedIntoId(form.source),
        }
      : { payee: form.label, type: form.type, schedule: "One-time", dueDate: date, amount, status: hasCome ? "Paid" : "Pending", autopay: form.autopay, description: form.description, ...paidFromFields(form.source) };

    apiFetch(`${API_BASE}/${endpoint}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then((res) => {
        if (!res.ok) return res.json().then((data) => Promise.reject(new Error(data.error)));
        return res.json();
      })
      .then((created) => {
        if (isIncome) setIncome((prev) => [...prev, created]);
        else setBills((prev) => [...prev, created]);
        setForm(emptyForm());
        setShowForm(false);
        if (scheduled && hasCome) setAskReceived(created);
      })
      .catch((err) => setFormError(err.message || "Failed to save."))
      .finally(() => setSaving(false));
  };

  return (
    <div style={{ ...styles.page, backgroundColor: tokens.bg, fontFamily: fonts.body }}>
      <h2 style={{ ...styles.heading, fontFamily: fonts.display, color: tokens.textPrimary }}>Transactions</h2>
      <p style={{ ...styles.subheading, color: tokens.textMuted }}>
        Everything coming in and going out, together on one timeline.
      </p>

      <div style={styles.statsRow}>
        <div style={{ ...styles.statTile, backgroundColor: tokens.surface, border: `1px solid ${tokens.surfaceBorder}`, boxShadow: tokens.cardShadow }}>
          <span style={{ ...styles.statLabel, color: tokens.textMuted }}>Income</span>
          <span style={{ ...styles.statValue, fontFamily: fonts.mono, color: tokens.positive }}>
            ₹{totalIncome.toLocaleString()}
          </span>
        </div>
        <div style={{ ...styles.statTile, backgroundColor: tokens.surface, border: `1px solid ${tokens.surfaceBorder}`, boxShadow: tokens.cardShadow }}>
          <span style={{ ...styles.statLabel, color: tokens.textMuted }}>Expenses</span>
          <span style={{ ...styles.statValue, fontFamily: fonts.mono, color: tokens.negative }}>
            ₹{totalExpenses.toLocaleString()}
          </span>
        </div>
        <div style={{ ...styles.statTile, backgroundColor: tokens.surface, border: `1px solid ${tokens.surfaceBorder}`, boxShadow: tokens.cardShadow }}>
          <span style={{ ...styles.statLabel, color: tokens.textMuted }}>Net</span>
          <span style={{ ...styles.statValue, fontFamily: fonts.mono, color: net >= 0 ? tokens.positive : tokens.negative }}>
            {net >= 0 ? "+" : "-"}₹{Math.abs(net).toLocaleString()}
          </span>
        </div>
      </div>

      <div style={{ ...styles.card, backgroundColor: tokens.surface, border: `1px solid ${tokens.surfaceBorder}`, boxShadow: tokens.cardShadow }}>
        {error && <div style={{ ...styles.banner, backgroundColor: tokens.pendingBg, color: tokens.pending }}>{error}</div>}

        {labelFilter && (
          <div style={{ ...styles.labelChip, backgroundColor: tokens.accentSoft }}>
            <span style={{ color: tokens.accent, fontWeight: 700, fontSize: 13 }}>
              Showing history for "{labelFilter}"
            </span>
            <button onClick={clearLabelFilter} style={{ ...styles.chipCloseBtn, color: tokens.accent }} aria-label="Clear filter">
              <X size={14} />
            </button>
          </div>
        )}

        <div style={styles.toolbar}>
          {PERIODS.map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              style={{
                ...styles.filterBtn,
                backgroundColor: period === p ? tokens.accent : tokens.neutralSoft,
                color: period === p ? tokens.accentContrast : tokens.textPrimary,
              }}
            >
              {p}
            </button>
          ))}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            style={{ ...styles.typeSelect, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
          >
            <option value="All">All Types</option>
            {ALL_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <button
            onClick={() => setShowForm((s) => !s)}
            style={{ ...styles.addBtn, backgroundColor: tokens.accent, color: tokens.accentContrast }}
          >
            <Plus size={15} strokeWidth={3} />
            Add
          </button>
          <SortSelect value={sortBy} onChange={setSortBy} />
          <button
            onClick={exportCsv}
            disabled={entries.length === 0}
            title="Download what's shown as a spreadsheet (CSV)"
            style={{ ...styles.filterBtn, display: "flex", alignItems: "center", gap: 6, backgroundColor: tokens.neutralSoft, color: tokens.textPrimary, opacity: entries.length ? 1 : 0.5 }}
          >
            <Download size={14} /> Export CSV
          </button>
        </div>

        {askReceived && (
          <div role="status" style={{ ...styles.banner, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", backgroundColor: tokens.accentSoft, color: tokens.textPrimary }}>
            <span style={{ flex: 1, minWidth: 240 }}>
              {askReceived.source} · ₹{askReceived.amount.toLocaleString()} was expected on {formatDate(askReceived.date)}. <strong>Have you received it?</strong>
              {askReceived.repeatUnit !== "once" && (
                <span style={{ fontWeight: 400, color: tokens.textMuted }}> Once it's received, the next one ({formatDate(nextOccurrence(askReceived.date, askReceived.repeatUnit, askReceived.repeatEvery))}) is lined up.</span>
              )}
            </span>
            <button onClick={() => answerReceived(true)} style={{ ...styles.saveBtn, backgroundColor: tokens.accent, color: tokens.accentContrast }}>Yes, mark as received</button>
            <button onClick={() => answerReceived(false)} style={{ ...styles.saveBtn, backgroundColor: "transparent", color: tokens.textPrimary, border: `1px solid ${tokens.surfaceBorder}` }}>Not yet</button>
          </div>
        )}

        {showForm && (
          <form onSubmit={handleFormSubmit} style={{ ...styles.form, border: `1px solid ${tokens.surfaceBorder}` }}>
            <div style={styles.kindToggle}>
              <button
                type="button"
                onClick={() => setForm({ ...emptyForm(), kind: "income" })}
                style={{
                  ...styles.kindBtn,
                  backgroundColor: form.kind === "income" ? tokens.positive : "transparent",
                  color: form.kind === "income" ? tokens.accentContrast : tokens.textMuted,
                }}
              >
                Income
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...emptyForm(), kind: "expense" })}
                style={{
                  ...styles.kindBtn,
                  backgroundColor: form.kind === "expense" ? tokens.negative : "transparent",
                  color: form.kind === "expense" ? "#fff" : tokens.textMuted,
                }}
              >
                Expense
              </button>
            </div>
            <input
              placeholder={form.kind === "income" ? "Source (e.g. Acme Corp)" : "Payee (e.g. Electric Company)"}
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              style={{ ...styles.formInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
            />
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              style={{ ...styles.formInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
            >
              <option value="" disabled>Type: Select</option>
              {(form.kind === "income" ? INCOME_TYPES : EXPENSE_TYPES).map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            {incomeScheduled && (
              <select
                aria-label="Frequency"
                value={form.repeatUnit}
                onChange={(e) => setForm({ ...form, repeatUnit: e.target.value })}
                style={{ ...inputStyle, color: form.repeatUnit ? tokens.textPrimary : tokens.textMuted }}
              >
                <option value="" disabled>Frequency: Select</option>
                {BILL_FREQUENCIES.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            )}
            {incomeScheduled && form.repeatUnit && form.repeatUnit !== "once" && (
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13.5, color: tokens.textPrimary }}>
                Every
                <input
                  type="number"
                  min="1"
                  max="365"
                  aria-label="Repeats every"
                  value={form.repeatEvery}
                  onChange={(e) => setForm({ ...form, repeatEvery: e.target.value })}
                  style={{ ...inputStyle, width: 70, minWidth: 0, flex: "0 0 70px" }}
                />
                {repeatUnitWord(form.repeatUnit, form.repeatEvery)}
              </label>
            )}
            {incomeScheduled && form.repeatUnit === "years" && (
              <select aria-label="Comes in month" value={form.dueMonth} onChange={(e) => setForm({ ...form, dueMonth: e.target.value })} style={inputStyle}>
                {MONTH_NAMES.map((m, i) => (
                  <option key={m} value={i}>{m}</option>
                ))}
              </select>
            )}
            {incomeScheduled && (form.repeatUnit === "months" || form.repeatUnit === "years") && (
              <select aria-label="Comes on day" value={form.dueDay} onChange={(e) => setForm({ ...form, dueDay: e.target.value })} style={inputStyle}>
                {DAYS.map((d) => (
                  <option key={d} value={d}>{form.repeatUnit === "months" ? `Comes on day ${d}` : d}</option>
                ))}
              </select>
            )}
            {(!incomeScheduled || form.repeatUnit === "once" || form.repeatUnit === "days") && (
              <input
                type="date"
                aria-label="Date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                style={inputStyle}
              />
            )}
            <input
              type="number"
              step="0.01"
              placeholder="Amount"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              style={{ ...styles.formInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
            />
            {(bankAccountsOf(sources.assets).length > 0 || (form.kind === "expense" && sources.cards.length > 0)) && (
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13.5, color: tokens.textPrimary }}>
                {form.kind === "income" ? "Received into" : "Paid from"}
                <MoneySourceSelect
                  style={inputStyle}
                  value={form.source}
                  onChange={(v) => setForm({ ...form, source: v })}
                  assets={sources.assets}
                  cards={sources.cards}
                  accountsOnly={form.kind === "income"}
                  required={sourceRequired(form.date || todayIso(), sources.assets, sources.cards)}
                />
              </label>
            )}
            <input
              placeholder="Description (optional notes)"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              style={{ ...styles.formInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface, minWidth: "220px" }}
            />
            {form.kind === "expense" && (
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13.5, color: tokens.textPrimary, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={form.autopay}
                  onChange={(e) => setForm({ ...form, autopay: e.target.checked })}
                />
                Autopay
              </label>
            )}
            <button type="submit" disabled={saving} style={{ ...styles.saveBtn, backgroundColor: tokens.accent, color: tokens.accentContrast }}>
              {saving ? "Saving..." : "Save"}
            </button>
            {formError && <span style={{ ...styles.formError, color: tokens.negative }}>{formError}</span>}
          </form>
        )}

        {settle && (
          <div role="dialog" style={{ ...styles.banner, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", backgroundColor: tokens.accentSoft, color: tokens.textPrimary }}>
            <span style={{ flex: 1, minWidth: 220 }}>
              {settle.kind === "lent"
                ? `${settle.item.label} paid back ₹${settle.item.amount.toLocaleString()}. Which account did it go into?`
                : `Paying back ₹${settle.item.amount.toLocaleString()} to ${settle.item.label}. Where did you pay it from?`}
            </span>
            <MoneySourceSelect
              style={inputStyle}
              value={settle.source}
              onChange={(v) => setSettle({ ...settle, source: v })}
              assets={sources.assets}
              cards={sources.cards}
              accountsOnly={settle.kind === "lent"}
              required={settleRequired}
            />
            <button onClick={confirmSettle} disabled={settleRequired && !settle.source} style={{ ...styles.saveBtn, backgroundColor: tokens.accent, color: tokens.accentContrast, opacity: settleRequired && !settle.source ? 0.6 : 1 }}>
              {settle.kind === "lent" ? "Got it back" : "Paid it back"}
            </button>
            <button onClick={() => setSettle(null)} style={{ ...styles.saveBtn, backgroundColor: "transparent", color: tokens.textPrimary, border: `1px solid ${tokens.surfaceBorder}` }}>Cancel</button>
          </div>
        )}

        {loading && <p style={{ color: tokens.textMuted, fontSize: 14 }}>Loading transactions...</p>}
        {!loading && !error && entries.length === 0 && (
          <p style={{ color: tokens.textMuted, fontSize: 14 }}>
            No transactions in {periodLabel(period).toLowerCase()}.
          </p>
        )}

        {!loading && entries.length > 0 && (
          <div style={{ position: "relative" }}>
            <div style={{ position: "absolute", left: 15, top: 8, bottom: 8, width: 2, backgroundColor: tokens.surfaceBorder }} />
            {entries.map((item, i) => {
              const isIncome = item.kind === "income";
              const dotColor = isIncome ? tokens.positive : tokens.negative;
              const isSettled = isIncome ? item.status === "Received" : item.status === "Paid";
              return (
                <div key={item.id} style={{ display: "flex", gap: 16, paddingBottom: i === entries.length - 1 ? 0 : 22 }}>
                  <div style={{ width: 32, display: "flex", justifyContent: "center", flexShrink: 0 }}>
                    <div
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        marginTop: 3,
                        backgroundColor: dotColor,
                        border: `3px solid ${tokens.surface}`,
                        boxShadow: `0 0 0 2px ${dotColor}`,
                      }}
                    />
                  </div>
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                      flexWrap: "wrap",
                      paddingBottom: 14,
                      borderBottom: i === entries.length - 1 ? "none" : `1px solid ${tokens.surfaceBorder}`,
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 600, color: tokens.textMuted }}>
                        {formatDate(item.date)}
                      </span>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 15, fontWeight: 700, color: tokens.textPrimary }}>{item.label}</span>
                        <span
                          style={{
                            fontSize: 11.5,
                            fontWeight: 700,
                            padding: "2px 9px",
                            borderRadius: 999,
                            backgroundColor: isIncome ? tokens.positiveBg : tokens.pendingBg,
                            color: isIncome ? tokens.positive : tokens.negative,
                          }}
                        >
                          {isIncome ? "Income" : "Expense"}
                        </span>
                        <span
                          style={{
                            fontSize: 11.5,
                            fontWeight: 600,
                            padding: "2px 9px",
                            borderRadius: 999,
                            backgroundColor: tokens.neutralSoft,
                            color: tokens.textMuted,
                          }}
                        >
                          {item.type}
                        </span>
                        {item.autopay && (
                          <span
                            style={{
                              fontSize: 10.5,
                              fontWeight: 700,
                              padding: "2px 7px",
                              borderRadius: 999,
                              backgroundColor: tokens.accentSoft,
                              color: tokens.accent,
                            }}
                          >
                            AUTOPAY
                          </span>
                        )}
                      </div>
                      {!!item.description && (
                        <span style={{ fontSize: 12.5, color: tokens.textMuted }}>{item.description}</span>
                      )}
                      {!isIncome && item.paidFromKind && (
                        <span style={{ fontSize: 12.5, color: tokens.textMuted }}>Paid from {paidFromName(item, sources)}</span>
                      )}
                      {(isIncome ? missingReceivedInto(item.raw, sources.assets, sources.cards) : missingPaidFrom(item.raw, sources.assets, sources.cards)) && (
                        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, color: tokens.pending }}>
                          {isIncome ? "Which account did it go into?" : "Where was it paid from?"}
                          <MoneySourceSelect
                            style={{ ...inputStyle, padding: "4px 8px", fontSize: 12.5 }}
                            value=""
                            onChange={(v) => setSource(item, v)}
                            assets={sources.assets}
                            cards={sources.cards}
                            accountsOnly={isIncome}
                            required
                          />
                        </label>
                      )}
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                      {item.type === "Borrowed" && item.status === "Received" &&
                        (item.repaid ? (
                          <span style={{ fontSize: 11.5, fontWeight: 700, padding: "3px 9px", borderRadius: 999, backgroundColor: tokens.positiveBg, color: tokens.positive }}>Paid back</span>
                        ) : (
                          <button
                            onClick={() => setSettle({ item, kind: "borrowed", source: "" })}
                            title="You paid it back: record it (not counted as spending)"
                            style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 999, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, backgroundColor: tokens.pendingBg, color: tokens.pending }}
                          >
                            <CornerDownLeft size={13} /> Paid it back
                          </button>
                        ))}
                      {item.type === "Lent" &&
                        (item.repaid ? (
                          <span style={{ fontSize: 11.5, fontWeight: 700, padding: "3px 9px", borderRadius: 999, backgroundColor: tokens.positiveBg, color: tokens.positive }}>Paid back</span>
                        ) : (
                          <button
                            onClick={() => setSettle({ item, kind: "lent", source: "" })}
                            title="They paid you back: record it as money received"
                            style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 999, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, backgroundColor: tokens.positiveBg, color: tokens.positive }}
                          >
                            <CornerDownLeft size={13} /> Got it back
                          </button>
                        ))}
                      <span style={{ fontFamily: fonts.mono, fontWeight: 700, fontSize: 15, color: dotColor }}>
                        {isIncome ? "+" : "-"}₹{item.amount.toLocaleString()}
                      </span>
                      <button
                        onClick={() => handleToggleStatus(item)}
                        title={isSettled ? `Mark as ${isIncome ? "upcoming" : "pending"}` : `Mark as ${isIncome ? "received" : "paid"}`}
                        style={{
                          ...styles.iconBtn,
                          backgroundColor: isSettled ? tokens.positiveBg : tokens.neutralSoft,
                          color: isSettled ? tokens.positive : tokens.textMuted,
                        }}
                      >
                        <Check size={14} strokeWidth={3} />
                      </button>
                      <button
                        onClick={() => handleDelete(item)}
                        aria-label={`Delete ${item.label}`}
                        style={{ ...styles.iconBtn, backgroundColor: "transparent", color: tokens.negative }}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  page: {
    padding: "24px",
    minHeight: "100vh",
    transition: "background-color 0.2s ease",
  },
  heading: {
    fontSize: "24px",
    fontWeight: "700",
    margin: 0,
  },
  subheading: {
    fontSize: "14px",
    margin: "6px 0 20px",
  },
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 16,
    marginBottom: 20,
    maxWidth: 620,
  },
  statTile: {
    borderRadius: 14,
    padding: "16px 18px",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  statLabel: {
    fontSize: 12.5,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  statValue: {
    fontSize: 22,
    fontWeight: 700,
  },
  card: {
    padding: "24px",
    borderRadius: "16px",
    transition: "background-color 0.2s ease, border-color 0.2s ease",
  },
  banner: {
    padding: "12px 16px",
    borderRadius: "10px",
    fontSize: "13.5px",
    fontWeight: 600,
    marginBottom: "16px",
  },
  labelChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 14px",
    borderRadius: 999,
    marginBottom: 16,
  },
  chipCloseBtn: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    display: "flex",
    padding: 0,
  },
  typeSelect: {
    padding: "9px 12px",
    borderRadius: 10,
    border: "1px solid",
    fontSize: 13.5,
    fontWeight: 600,
    cursor: "pointer",
  },
  toolbar: {
    display: "flex",
    gap: "10px",
    marginBottom: "20px",
    flexWrap: "wrap",
    alignItems: "center",
  },
  filterBtn: {
    padding: "10px 16px",
    border: "none",
    borderRadius: "20px",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "14px",
  },
  addBtn: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "10px 16px",
    border: "none",
    borderRadius: "20px",
    cursor: "pointer",
    fontWeight: "700",
    fontSize: "14px",
    marginLeft: "auto",
  },
  form: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    alignItems: "center",
    padding: "16px",
    borderRadius: "12px",
    marginBottom: "20px",
  },
  kindToggle: {
    display: "flex",
    gap: 6,
  },
  kindBtn: {
    border: "none",
    borderRadius: 8,
    padding: "8px 14px",
    fontWeight: 700,
    fontSize: 13,
    cursor: "pointer",
  },
  formInput: {
    padding: "8px 12px",
    borderRadius: "8px",
    border: "1px solid",
    fontSize: "14px",
    minWidth: "140px",
  },
  saveBtn: {
    padding: "9px 18px",
    border: "none",
    borderRadius: "8px",
    fontWeight: "700",
    fontSize: "14px",
    cursor: "pointer",
  },
  formError: {
    fontSize: "13px",
    fontWeight: 600,
  },
  iconBtn: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
  },
};

export default Transactions;
